import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/hooks/use-auth";
import { apiFetch } from "@/lib/api";
import {
  useListLocations, useGetTrainerLocations, useUpdateTrainerLocations,
  useGetTrainerClientRates, useCreateTrainerClientRate, useDeleteTrainerClientRate,
  useGetTrainerStudents, useGetGymPricingPolicy,
  getGetTrainerClientRatesQueryKey,
} from "@workspace/api-client-react";
import type { TrainerClientRate, GetTrainerStudents200Item } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Save, User, Mail, MapPin, AlertTriangle, Star, DollarSign, Plus, Trash2,
  Users, MessageSquare, Smartphone, Bell, CheckCircle2, XCircle, Clock,
  CalendarClock, Banknote, Wallet, WifiOff, ArrowLeftRight, ChevronsLeftRight,
  ShieldAlert, Video, ThumbsUp,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { ChangePasswordCard } from "@/components/change-password-card";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { ImageUpload } from "@/components/image-upload";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function TrainerProfile() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    bio: "",
    specialties: "",
    phone: "",
    introVideoUrl: "",
    gender: "",
    smsEnabled: false,
    emailEnabled: true,
    pushEnabled: true,
  });

  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        bio: (user as any).bio || "",
        specialties: ((user as any).specialties || []).join(", "),
        phone: (user as any).phone || "",
        introVideoUrl: (user as any).introVideoUrl || "",
        gender: (user as any).gender || "",
        smsEnabled: (user as any).smsEnabled ?? false,
        emailEnabled: (user as any).emailEnabled ?? true,
        pushEnabled: (user as any).pushEnabled ?? true,
      });
    }
  }, [user]);

  async function handleSave() {
    if (!user) return;
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      toast({ title: "First name, last name, and email are required", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await apiFetch(`/trainers/${user.id}/profile`, {
        method: "PUT",
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          bio: form.bio,
          specialties: form.specialties.split(",").map((s) => s.trim()).filter(Boolean),
          phone: form.phone,
          introVideoUrl: form.introVideoUrl,
          gender: form.gender || null,
          smsEnabled: form.smsEnabled,
          emailEnabled: form.emailEnabled,
          pushEnabled: form.pushEnabled,
        }),
      });
      await refreshUser();
      toast({ title: "Profile updated" });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12 max-w-2xl">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">My Profile</h1>
          <p className="text-muted-foreground">Update your public trainer profile</p>
        </div>

        <Card className="glass-panel border-white/5">
          <CardHeader>
            <CardTitle className="text-xl font-display text-white flex items-center gap-2"><User className="w-5 h-5" /> Profile Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5">
              <ImageUpload
                currentUrl={user?.avatarUrl}
                onUpload={() => { refreshUser(); }}
                uploadEndpoint="/uploads/avatar"
                label="Change Photo"
                shape="circle"
                size="md"
                fallback={
                  <div className="w-full h-full bg-primary/20 flex items-center justify-center text-2xl font-bold text-primary">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </div>
                }
              />
              <div className="text-xs text-muted-foreground">JPG, PNG, WebP · Max 5MB</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-white mb-2 block">First Name</label>
                <Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} placeholder="First name" className="bg-background border-white/10" />
              </div>
              <div>
                <label className="text-sm font-medium text-white mb-2 block">Last Name</label>
                <Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} placeholder="Last name" className="bg-background border-white/10" />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-white mb-2 block flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email</label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="bg-background border-white/10" />
            </div>

            <div>
              <label className="text-sm font-medium text-white mb-2 block">Phone</label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" className="bg-background border-white/10" />
            </div>

            <div>
              <label className="text-sm font-medium text-white mb-2 block">Bio</label>
              <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Tell students about your experience..." rows={4} className="bg-background border-white/10" />
            </div>

            <div>
              <label className="text-sm font-medium text-white mb-2 block">Specialties</label>
              <Input value={form.specialties} onChange={(e) => setForm({ ...form, specialties: e.target.value })} placeholder="BJJ, Muay Thai, Wrestling (comma separated)" className="bg-background border-white/10" />
            </div>

            <div>
              <label className="text-sm font-medium text-white mb-2 block">Intro Video URL</label>
              <Input value={form.introVideoUrl} onChange={(e) => setForm({ ...form, introVideoUrl: e.target.value })} placeholder="https://youtube.com/watch?v=..." className="bg-background border-white/10" />
            </div>

            <div>
              <label className="text-sm font-medium text-white mb-2 block">Gender</label>
              <select
                value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
                className="w-full bg-background border border-white/10 rounded-md px-3 py-2 text-sm text-white"
              >
                <option value="">Not specified</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">Used to help students with trainer preferences when booking.</p>
            </div>

            <div className="space-y-2 pt-2 border-t border-white/5">
              <p className="text-sm font-medium text-white">Notification Preferences</p>
              <p className="text-xs text-muted-foreground">Choose how you want to receive booking and session alerts</p>
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-white/5 border border-white/5">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-blue-400 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-white">Email</p>
                    <p className="text-xs text-muted-foreground">Booking requests, confirmations, reminders</p>
                  </div>
                </div>
                <Switch checked={form.emailEnabled} onCheckedChange={(v) => setForm({ ...form, emailEnabled: v })} />
              </div>
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-white/5 border border-white/5">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-4 h-4 text-green-400 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-white">SMS</p>
                    <p className="text-xs text-muted-foreground">Text alerts for new bookings and day-of reminders</p>
                  </div>
                </div>
                <Switch checked={form.smsEnabled} onCheckedChange={(v) => setForm({ ...form, smsEnabled: v })} />
              </div>
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-white/5 border border-white/5">
                <div className="flex items-center gap-3">
                  <Smartphone className="w-4 h-4 text-purple-400 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-white">Push Notifications</p>
                    <p className="text-xs text-muted-foreground">Instant alerts via the mobile app</p>
                  </div>
                </div>
                <Switch checked={form.pushEnabled} onCheckedChange={(v) => setForm({ ...form, pushEnabled: v })} />
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full bg-primary text-primary-foreground hover:bg-primary/80">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Profile"}
            </Button>
          </CardContent>
        </Card>

        <MyLocationsCard />
        <ClientRatesCard />
        <TrainerNotifPrefsCard />
        <ChangePasswordCard />
      </div>
    </AppLayout>
  );
}

function MyLocationsCard() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();

  const { data: gymLocations = [], isLoading: locationsLoading } = useListLocations(
    user?.gymId || "",
    { query: { enabled: !!user?.gymId } }
  );

  const { data: trainerLocationIds = [], refetch: refetchTrainerLocations, isFetched: trainerLocationsLoaded } = useGetTrainerLocations(
    user?.id || "",
    { query: { enabled: !!user?.id } }
  );

  const updateLocations = useUpdateTrainerLocations();

  const activeLocations = useMemo(
    () => gymLocations.filter((l) => l.isActive),
    [gymLocations]
  );

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [primaryId, setPrimaryId] = useState<string | null>(null);
  const [savingLocations, setSavingLocations] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    locationName: string;
    slotCount: number;
    onConfirm: () => void;
  }>({ open: false, locationName: "", slotCount: 0, onConfirm: () => {} });

  useEffect(() => {
    if (trainerLocationsLoaded && activeLocations.length > 0 && !initialized) {
      setSelectedIds([...trainerLocationIds]);
      setPrimaryId(user?.primaryLocationId || null);
      setInitialized(true);
    }
  }, [trainerLocationIds, trainerLocationsLoaded, user, initialized, activeLocations]);

  const hasChanges = useMemo(() => {
    if (!initialized) return false;
    const currentPrimary = user?.primaryLocationId || null;
    const sortedSelected = [...selectedIds].sort();
    const sortedOriginal = [...trainerLocationIds].sort();
    if (sortedSelected.length !== sortedOriginal.length) return true;
    if (sortedSelected.some((id, i) => id !== sortedOriginal[i])) return true;
    if (primaryId !== currentPrimary) return true;
    return false;
  }, [selectedIds, trainerLocationIds, primaryId, user, initialized]);

  const checkAvailabilitySlots = useCallback(async (locationId: string): Promise<number> => {
    if (!user?.id) return 0;
    try {
      const slots = await apiFetch(`/availability/trainer/${user.id}?locationId=${locationId}`);
      return Array.isArray(slots) ? slots.length : 0;
    } catch {
      return 0;
    }
  }, [user?.id]);

  const handleToggleLocation = useCallback(async (locationId: string, checked: boolean) => {
    if (checked) {
      setSelectedIds((prev) => prev.includes(locationId) ? prev : [...prev, locationId]);
    } else {
      const slotCount = await checkAvailabilitySlots(locationId);
      const location = activeLocations.find((l) => l.id === locationId);

      if (slotCount > 0) {
        setConfirmDialog({
          open: true,
          locationName: location?.name || "this location",
          slotCount,
          onConfirm: () => {
            setSelectedIds((prev) => prev.filter((id) => id !== locationId));
            if (primaryId === locationId) {
              const remaining = selectedIds.filter((id) => id !== locationId);
              setPrimaryId(remaining.length > 0 ? remaining[0] : null);
            }
            setConfirmDialog((prev) => ({ ...prev, open: false }));
          },
        });
      } else {
        setSelectedIds((prev) => prev.filter((id) => id !== locationId));
        if (primaryId === locationId) {
          const remaining = selectedIds.filter((id) => id !== locationId);
          setPrimaryId(remaining.length > 0 ? remaining[0] : null);
        }
      }
    }
  }, [activeLocations, checkAvailabilitySlots, primaryId, selectedIds]);

  const handleSetPrimary = useCallback((locationId: string) => {
    setPrimaryId(locationId);
  }, []);

  async function handleSaveLocations() {
    if (!user) return;
    setSavingLocations(true);
    try {
      await updateLocations.mutateAsync({
        id: user.id,
        data: { locationIds: selectedIds },
      });

      await apiFetch(`/trainers/${user.id}/profile`, {
        method: "PUT",
        body: JSON.stringify({
          primaryLocationId: selectedIds.length > 0 ? (selectedIds.includes(primaryId || "") ? primaryId : selectedIds[0]) : null,
        }),
      });

      await refetchTrainerLocations();
      await refreshUser();
      toast({ title: "Locations updated" });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSavingLocations(false);
    }
  }

  if (locationsLoading) {
    return (
      <Card className="glass-panel border-white/5">
        <CardHeader>
          <CardTitle className="text-xl font-display text-white flex items-center gap-2">
            <MapPin className="w-5 h-5" /> My Locations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading locations...</p>
        </CardContent>
      </Card>
    );
  }

  if (activeLocations.length === 0) {
    return (
      <Card className="glass-panel border-white/5">
        <CardHeader>
          <CardTitle className="text-xl font-display text-white flex items-center gap-2">
            <MapPin className="w-5 h-5" /> My Locations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No locations have been set up for your gym yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="glass-panel border-white/5">
        <CardHeader>
          <CardTitle className="text-xl font-display text-white flex items-center gap-2">
            <MapPin className="w-5 h-5" /> My Locations
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Select which locations you're available at and choose your primary location.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {activeLocations.map((location) => {
            const isSelected = selectedIds.includes(location.id);
            const isPrimary = primaryId === location.id;

            return (
              <div
                key={location.id}
                className={`flex items-center justify-between p-4 rounded-xl transition-all ${
                  isSelected ? "bg-primary/5 border border-primary/20" : "bg-white/5 border border-white/5"
                }`}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <Switch
                    checked={isSelected}
                    onCheckedChange={(checked) => handleToggleLocation(location.id, !!checked)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-medium truncate ${isSelected ? "text-white" : "text-muted-foreground"}`}>
                        {location.name}
                      </p>
                      {isPrimary && isSelected && (
                        <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full flex items-center gap-1 shrink-0">
                          <Star className="w-3 h-3" /> Primary
                        </span>
                      )}
                    </div>
                    {location.address && (
                      <p className="text-xs text-muted-foreground truncate">
                        {location.address}{location.city ? `, ${location.city}` : ""}{location.state ? `, ${location.state}` : ""}
                      </p>
                    )}
                  </div>
                </div>
                {isSelected && !isPrimary && selectedIds.length > 1 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleSetPrimary(location.id)}
                    className="text-muted-foreground hover:text-primary text-xs shrink-0 ml-2"
                  >
                    Set as Primary
                  </Button>
                )}
              </div>
            );
          })}

          <Button
            onClick={handleSaveLocations}
            disabled={savingLocations || !hasChanges}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/80 mt-2"
          >
            <Save className="w-4 h-4 mr-2" /> {savingLocations ? "Saving..." : "Save Locations"}
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog((prev) => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Remove Location?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You have {confirmDialog.slotCount} availability slot{confirmDialog.slotCount !== 1 ? "s" : ""} at <strong>{confirmDialog.locationName}</strong>. Removing yourself from this location means those slots will no longer apply. Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDialog.onConfirm}>
              Remove Location
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ClientRatesCard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const gymId = user?.gymId || "";

  const { data: pricingPolicy } = useGetGymPricingPolicy(gymId, {
    query: { enabled: !!gymId },
  });

  const { data: clientRates = [], isLoading: ratesLoading } = useGetTrainerClientRates(
    user?.id || "",
    { query: { enabled: !!user?.id } }
  );

  const { data: students = [] } = useGetTrainerStudents(
    user?.id || "",
    { query: { enabled: !!user?.id } }
  );

  const createRate = useCreateTrainerClientRate();
  const deleteRate = useDeleteTrainerClientRate();

  const [addOpen, setAddOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [rateValue, setRateValue] = useState("");

  const allowTrainerPricing = pricingPolicy?.allowTrainerPricing ?? true;
  const canSelfPrice = pricingPolicy?.pricingMode === "TRAINER_SET" ||
    (pricingPolicy?.pricingMode === "HYBRID" && user?.canSetOwnRate);

  const existingStudentIds = useMemo(
    () => new Set((clientRates as TrainerClientRate[]).map((r) => r.studentId)),
    [clientRates]
  );

  const availableStudents = useMemo(
    () => (students as GetTrainerStudents200Item[]).filter((s) => !existingStudentIds.has(s.id)),
    [students, existingStudentIds]
  );

  const invalidateRates = () => {
    queryClient.invalidateQueries({ queryKey: getGetTrainerClientRatesQueryKey(user?.id || "") });
  };

  const handleAdd = async () => {
    if (!selectedStudentId || !rateValue) {
      toast({ title: "Please select a student and enter a rate", variant: "destructive" });
      return;
    }
    const rate = parseFloat(rateValue);
    if (isNaN(rate) || rate <= 0) {
      toast({ title: "Please enter a valid rate", variant: "destructive" });
      return;
    }
    try {
      await createRate.mutateAsync({
        id: user!.id,
        data: { studentId: selectedStudentId, hourlyRate: rate },
      });
      invalidateRates();
      setAddOpen(false);
      setSelectedStudentId("");
      setRateValue("");
      toast({ title: "Client rate saved" });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to save client rate";
      toast({ title: msg, variant: "destructive" });
    }
  };

  const handleDelete = async (rateId: string) => {
    try {
      await deleteRate.mutateAsync({ id: user!.id, rateId });
      invalidateRates();
      toast({ title: "Client rate removed" });
    } catch {
      toast({ title: "Failed to remove client rate", variant: "destructive" });
    }
  };

  if (!allowTrainerPricing || !canSelfPrice) {
    return null;
  }

  const globalRate = user?.hourlyRate ?? null;

  return (
    <>
      <Card className="glass-panel border-white/5">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-display text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5" /> Client Rates
            </CardTitle>
            <Button size="sm" onClick={() => setAddOpen(true)} className="gap-1.5">
              <Plus className="w-4 h-4" /> Add Override
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Set custom hourly rates for individual students. Students without an override will use your
            global rate{globalRate != null ? ` (${formatCurrency(globalRate)}/hr)` : ""}.
          </p>
        </CardHeader>
        <CardContent>
          {ratesLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : clientRates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center gap-2">
              <Users className="w-8 h-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No per-client rate overrides set. All students use your global rate.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {(clientRates as TrainerClientRate[]).map((rate) => (
                <div
                  key={rate.id}
                  className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/5"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-sm font-bold border border-white/10 shrink-0">
                      {rate.student?.firstName?.[0]}{rate.student?.lastName?.[0]}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">
                        {rate.student?.firstName} {rate.student?.lastName}
                      </p>
                      <p className="text-xs text-muted-foreground">{rate.student?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm font-mono font-bold text-white">{formatCurrency(rate.hourlyRate)}/hr</p>
                      {globalRate != null && rate.hourlyRate !== globalRate && (
                        <p className="text-[10px] text-muted-foreground">
                          {rate.hourlyRate < globalRate ? "Discount" : "Premium"} vs {formatCurrency(globalRate)}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => handleDelete(rate.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="glass-panel border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-wide">
              Add Client Rate Override
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-white">Student</label>
              {availableStudents.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No more students available. All your students already have rate overrides, or you have no session history yet.
                </p>
              ) : (
                <Select value={selectedStudentId} onValueChange={setSelectedStudentId}>
                  <SelectTrigger className="bg-background border-white/10">
                    <SelectValue placeholder="Select a student" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableStudents.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.firstName} {s.lastName} ({s.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-white">Hourly Rate ($)</label>
              <Input
                type="number"
                min={0}
                step={5}
                value={rateValue}
                onChange={(e) => setRateValue(e.target.value)}
                placeholder={globalRate != null ? String(globalRate) : "e.g., 75"}
                className="bg-background border-white/10"
              />
              {globalRate != null && (
                <p className="text-xs text-muted-foreground">
                  Your global rate is {formatCurrency(globalRate)}/hr
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAdd}
              disabled={createRate.isPending || !selectedStudentId || !rateValue}
            >
              {createRate.isPending ? "Saving..." : "Save Rate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Trainer Notification Preferences Card ────────────────────────────────────

type ChannelPrefs = { email: boolean; sms: boolean; push: boolean };
type AllNotifPrefs = Record<string, ChannelPrefs>;
const DEFAULT_PREFS: ChannelPrefs = { email: true, sms: true, push: true };

function getPrefs(all: AllNotifPrefs, type: string): ChannelPrefs {
  return { ...DEFAULT_PREFS, ...(all[type] ?? {}) };
}

type TrainerNotifGroup = {
  label: string;
  items: { type: string; label: string; description: string; icon: React.ReactNode }[];
};

const TRAINER_NOTIF_GROUPS: TrainerNotifGroup[] = [
  {
    label: "Bookings & Sessions",
    items: [
      { type: "BOOKING_RECEIVED",       label: "New Session Request",  description: "A student requested to book a session with you",        icon: <CheckCircle2 className="w-4 h-4 text-green-400" /> },
      { type: "BOOKING_CANCELLED",      label: "Session Cancelled",    description: "A confirmed session was cancelled",                     icon: <XCircle className="w-4 h-4 text-red-400" /> },
      { type: "SESSION_REMINDER",       label: "Session Reminder",     description: "Reminder before an upcoming session",                   icon: <CalendarClock className="w-4 h-4 text-amber-400" /> },
      { type: "DROPIN_BOOKING_RECEIVED",label: "Drop-In Booking",      description: "A student booked a spot in your drop-in class",         icon: <Users className="w-4 h-4 text-blue-400" /> },
      { type: "RATING_RECEIVED",        label: "Rating Received",      description: "A student left you a rating after a session",           icon: <ThumbsUp className="w-4 h-4 text-yellow-400" /> },
    ],
  },
  {
    label: "Scheduling Negotiations",
    items: [
      { type: "TIME_PROPOSAL_RECEIVED",    label: "Time Proposed",         description: "A student suggested alternate session times",           icon: <ArrowLeftRight className="w-4 h-4 text-blue-400" /> },
      { type: "TIME_PROPOSAL_ACCEPTED",    label: "Proposal Accepted",     description: "Student accepted your time suggestion",                 icon: <CheckCircle2 className="w-4 h-4 text-green-400" /> },
      { type: "TIME_PROPOSAL_DECLINED",    label: "Proposal Declined",     description: "Student declined your time suggestion",                 icon: <XCircle className="w-4 h-4 text-red-400" /> },
      { type: "COUNTER_PROPOSAL_RECEIVED", label: "Counter Proposed",      description: "Student sent a counter time proposal",                  icon: <ChevronsLeftRight className="w-4 h-4 text-violet-400" /> },
      { type: "NEGOTIATION_LIMIT_REACHED", label: "Negotiation Limit Hit", description: "Max rounds of back-and-forth scheduling reached",       icon: <Clock className="w-4 h-4 text-orange-400" /> },
    ],
  },
  {
    label: "Earnings & Payouts",
    items: [
      { type: "PAYMENT_CONFIRMED", label: "Payment Received", description: "Payment confirmed for a completed session",        icon: <Banknote className="w-4 h-4 text-green-400" /> },
      { type: "PAYOUT_SENT",       label: "Payout Sent",      description: "A payout has been transferred to your account",   icon: <Wallet className="w-4 h-4 text-primary" /> },
      { type: "PAYOUT_FAILED",     label: "Payout Failed",    description: "A payout couldn't be processed",                  icon: <Banknote className="w-4 h-4 text-red-400" /> },
    ],
  },
  {
    label: "Alerts",
    items: [
      { type: "NOSHOW_FLAGGED",   label: "No-Show Recorded",  description: "A session was flagged as a no-show",            icon: <AlertTriangle className="w-4 h-4 text-orange-400" /> },
      { type: "NOSHOW_ESCALATED", label: "No-Show Escalated", description: "A no-show has been escalated to the gym admin",  icon: <ShieldAlert className="w-4 h-4 text-red-400" /> },
    ],
  },
];

function TrainerNotifPrefsCard() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [notifPrefs, setNotifPrefs] = useState<AllNotifPrefs>({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (user && !loaded) {
      setNotifPrefs((user as any).notificationPreferences ?? {});
      setLoaded(true);
    }
  }, [user, loaded]);

  function setChannel(type: string, channel: keyof ChannelPrefs, value: boolean) {
    setNotifPrefs(prev => ({
      ...prev,
      [type]: { ...getPrefs(prev, type), [channel]: value },
    }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await apiFetch("/auth/notification-preferences", {
        method: "PATCH",
        body: JSON.stringify({ preferences: notifPrefs }),
      });
      await refreshUser();
      toast({ title: "Notification preferences saved" });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Something went wrong";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="glass-panel border-white/5">
      <CardHeader>
        <CardTitle className="text-xl font-display text-white flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary" /> Notification Preferences
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-muted-foreground">
          Fine-tune which channels each alert uses. The global channel switches above must also be on for a channel to fire.
        </p>

        {/* Column header */}
        <div className="flex items-center gap-3 px-1">
          <div className="flex-1" />
          <div className="flex items-center gap-5 pr-0.5">
            <span className="w-8 text-center"><Mail className="w-3.5 h-3.5 text-blue-400 mx-auto" /></span>
            <span className="w-8 text-center"><MessageSquare className="w-3.5 h-3.5 text-green-400 mx-auto" /></span>
            <span className="w-8 text-center"><Smartphone className="w-3.5 h-3.5 text-purple-400 mx-auto" /></span>
          </div>
        </div>

        {TRAINER_NOTIF_GROUPS.map((group) => (
          <div key={group.label} className="space-y-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest px-1 mb-2">{group.label}</p>
            <div className="rounded-xl border border-white/5 overflow-hidden divide-y divide-white/5">
              {group.items.map(({ type, label, description, icon }) => {
                const prefs = getPrefs(notifPrefs, type);
                return (
                  <div key={type} className="flex items-center gap-3 px-4 py-3 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                    <div className="shrink-0">{icon}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{label}</p>
                      <p className="text-xs text-muted-foreground truncate">{description}</p>
                    </div>
                    <div className="flex items-center gap-5 shrink-0">
                      <div className="w-8 flex justify-center">
                        <Switch checked={prefs.email} onCheckedChange={(v) => setChannel(type, "email", v)} className="scale-75" />
                      </div>
                      <div className="w-8 flex justify-center">
                        <Switch checked={prefs.sms} onCheckedChange={(v) => setChannel(type, "sms", v)} className="scale-75" />
                      </div>
                      <div className="w-8 flex justify-center">
                        <Switch checked={prefs.push} onCheckedChange={(v) => setChannel(type, "push", v)} className="scale-75" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <Button onClick={handleSave} disabled={saving} className="w-full">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Notification Preferences"}
        </Button>
      </CardContent>
    </Card>
  );
}
